import { Component, Inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DeviceDetectorService } from 'ngx-device-detector';
import { JobService } from '../../job/services/job.service';
import { AbstractBiditComponent } from '../../shared/controls/abstract.bidit.component';
import { Crew } from '../../shared/models/crew.interface';
import { CrewPersonnel } from '../../shared/models/crew.personnel.interface';
import { FieldTechnician } from '../../shared/models/field.technician.interface';
import { WorkOrderCrew } from '../../shared/models/work.order.crew.interface';
import { WorkOrder } from '../../shared/models/work.order.interface';
import { WorkOrderLabor } from '../../shared/models/work.order.labor.interface';
import { LaborEdit } from '../models/labor.edit.interface';
import { WorkOrderService } from '../services/work.order.service';

export interface LaborDialogData {
  workOrder: WorkOrder;

  crews: Crew[];
  fieldTechnicians: FieldTechnician[];
}

@Component({
    selector: 'app-labor-dialog',
    templateUrl: './labor.dialog.html',
    styleUrls: ['./labor.dialog.scss']
})
export class LaborDialog extends AbstractBiditComponent {
  workOrderSqrFt: number;
  workOrderOverallLabor: number = null;
  hasLaborDetails: boolean = false;
  hasOverallLabor: boolean = false;

  addSelection: FieldTechnician;  
  labor: WorkOrderLabor[];
  availablePersonnel: FieldTechnician[];

  constructor(
    deviceDetectorService: DeviceDetectorService,
    private workOrderService: WorkOrderService,
    private jobService: JobService,
    public dialogRef: MatDialogRef<LaborDialog>,
    @Inject(MAT_DIALOG_DATA) public data: LaborDialogData) {
    super(deviceDetectorService);

    this.workOrderSqrFt = data.workOrder.workOrder_ActualSqrFtg;
    this.workOrderOverallLabor = data.workOrder.workOrder_TotalOverallLabor;
    this.labor = [];

    if (data.workOrder.workOrder_ActualSqrFtg == null) {
      this.workOrderSqrFt = 0;
      this.jobService.getJob(this.data.workOrder.quote_ID)
        .subscribe(job => {
          let workOrder = job.job.workOrders.find(wo => wo.workOrder_ID == this.data.workOrder.workOrder_ID);

          for (let area of job.jobAreaSummaries) {
            for (let quoteArea of area.quoteAreas) {
              let existingArea = workOrder.workOrderAreas.find(woa => woa.quoteArea_ID == quoteArea.quoteArea_ID);
              if (existingArea != null) this.workOrderSqrFt += quoteArea.acceptedItem.area_SqrFtg;
            }
          }          
      });
    }

    if (data.workOrder.workOrderLabor.length > 0) {
      this.hasLaborDetails = true;
      this.workOrderOverallLabor = 0;
      data.workOrder.workOrderLabor.forEach(wol => {
        this.labor.push(Object.assign({}, wol));
      });

      this.calculateRates();
      this.evaluateLabor();
      return;
    }

    if (data.workOrder.workOrder_TotalOverallLabor === null || data.workOrder.workOrder_TotalOverallLabor === undefined)
    data.workOrder.workOrderCrews.forEach(woc => {
      const crew = data.crews.find(c => c.crew_ID == woc.crew_ID);
      if (crew == null) return;

      crew.crewPersonnel.forEach(cp => {
        const laborEntry = this.labor.find(wol => wol.fieldTechnician_ID == cp.fieldTechnician_ID);

        if (laborEntry == null) {
          this.labor.push(this.makeLabor(cp.fieldTechnician));
        }
      });
    });

    data.workOrder.workOrderCrewOverrides.forEach(woco => {
      if (woco.override_IsRemoval) {
        const laborEntry = this.labor.find(wol =>
          wol.workOrder_ID == null && wol.fieldTechnician_ID == woco.fieldTechnician_ID);

        if (laborEntry != null) {
          const index = this.labor.indexOf(laborEntry);
          this.labor.splice(index, 1);
        }
      } else {
        const laborEntry = this.labor.find(wol => wol.fieldTechnician_ID == woco.fieldTechnician_ID);

        if (laborEntry == null) {
          this.labor.push(this.makeLabor(woco.overrideUser));
        }
      }
    });

    this.calculateRates();
    this.evaluateLabor();
  }

  evaluateLabor(): void {
    if (this.workOrderOverallLabor === 0) {
      this.workOrderOverallLabor = null;
    }

    this.hasOverallLabor = this.workOrderOverallLabor !== undefined && this.workOrderOverallLabor !== null;
    this.hasLaborDetails = this.labor.length > 0;
  }

  laborChanged(overallLabor: number) {
    this.workOrderOverallLabor = overallLabor;
    this.evaluateLabor();
  }

  makeLabor(fieldTech: FieldTechnician): WorkOrderLabor {
    return {
      fieldTechnician_ID: fieldTech.fieldTechnician_ID,
      workOrderLabor_HourlyRate: fieldTech.fieldTechnician_HourlyRate,
      fieldTechnician: fieldTech
    } as WorkOrderLabor;
  }

  addPersonnel(): void {
    const laborEntry = this.labor.find(wol => wol.fieldTechnician_ID == this.addSelection.fieldTechnician_ID);

    if (laborEntry == null) {
      this.labor.push(this.makeLabor(this.addSelection));
    }
    this.evaluateLabor();
  }

  removeTechnician(fieldTech: FieldTechnician): void {
    const laborEntry = this.labor.find(wol => wol.fieldTechnician_ID == fieldTech.fieldTechnician_ID);

    const index = this.labor.indexOf(laborEntry);
    this.labor.splice(index, 1);
    this.evaluateLabor();
  }

  updateLabor(form: NgForm): void {
    if (form.valid) {
      this.isRequesting = true;

    const edit = {
      workOrderSqrFt: this.workOrderSqrFt,
       workOrderOverallLabor: this.workOrderOverallLabor,
        labor: this.labor
      } as LaborEdit;
      
      this.workOrderService.saveLabor(this.data.workOrder.workOrder_ID, edit)
        .subscribe(
          {
            next: (data) => this.dialogRef.close(data),
            error: (error: string) => this.onError(error)
          });
    }
  }

  calculateRates(): void {
    this.labor.forEach(wol => {
      this.calculateRate(wol);
    });
  }

  calculateRate(wol: WorkOrderLabor): void {
    if (this.workOrderSqrFt == null ||
      wol.workOrderLabor_HoursWorked == null ||
      this.workOrderSqrFt == 0) {
      return;
    }

    if (wol.workOrderLabor_HourlyRate != null &&
      wol.workOrderLabor_HourlyRate != 0) {
      this.calculatePieceRate(wol);
    }

    if (wol.workOrderLabor_PieceRate != null &&
      wol.workOrderLabor_PieceRate != 0) {
      this.calculateHourlyRate(wol);
    }
  }

  calculatePieceRate(wol: WorkOrderLabor): void {
    if (this.workOrderSqrFt == null ||
      wol.workOrderLabor_HourlyRate == null ||
      wol.workOrderLabor_HoursWorked == null ||
      this.workOrderSqrFt == 0) {
      wol.workOrderLabor_PieceRate = 0;
      return;
    }

    wol.workOrderLabor_PieceRate = (wol.workOrderLabor_HourlyRate * wol.workOrderLabor_HoursWorked) /
      this.workOrderSqrFt;
  }

  calculateHourlyRate(wol: WorkOrderLabor): void {
    if (this.workOrderSqrFt == null ||
      wol.workOrderLabor_PieceRate == null ||
      wol.workOrderLabor_HoursWorked == null ||
      this.workOrderSqrFt == 0) {
      return;
    }

    wol.workOrderLabor_HourlyRate = (wol.workOrderLabor_PieceRate * this.workOrderSqrFt) /
      wol.workOrderLabor_HoursWorked;
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
